import { SearchComponent } from "@/App/Tenants/Search";
import { CreateLoaderTable } from "@/components/Loaders";
import { Main } from "@/components/Main";
import { useCallback, useEffect, useState } from "react";
import { ApiDataProps } from "../Tenants";
import { TypePageActions } from "../Plans";
import { AddBtn } from "@/App/Reports/Add";
import { TableData } from "@/App/Reports/Table";
import { getReportsData, searchReportData } from "@/Api/reports";
import { usePermissions } from "@/hooks/usePermissions";
import { If } from "@/components/If";
import { RefreshCw, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/PageHeader";

export type TableDataProps = {
  id: number;
  uuid: string;
  name: string;
  report_type_id: number;
  file_path: string;
  file_name: string;
  start_date: string;
  end_date: string;
  unity_id: number | null;
  report_status_id: number;
  generated_by_user_id: number;
  tenant_id: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  unity: {
    id: number;
    name: string;
  } | null;
  report_status: {
    id: number;
    name: string;
  };
  report_type: {
    id: number;
    name: string;
  };
  generated_by_user: {
    uuid: string;
    id: number;
    name: string;
  };
  tenant: {
    uuid: string;
    id: number;
    name: string;
  };
};

export const PageList = () => {
  const [listData, setListData] = useState({} as ApiDataProps<TableDataProps>);
  const [state, setState] = useState("loading");
  const [isRefreshing, setIsRefreshing] = useState(false);
  const { hasPermission } = usePermissions();

  const fetchData = useCallback(async (page?: number) => {
    const { data } = await getReportsData(page ? { page } : undefined);
    setListData(data?.response);
  }, []);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchData(listData?.current_page || 1);
    setIsRefreshing(false);
  };

  useEffect(() => {
    (async () => {
      await fetchData();
      setState("production");
    })();
  }, [fetchData]);

  const pageActions: TypePageActions = {
    next: async () => {
      if (listData?.current_page < listData?.last_page) {
        setState("loading");

        const { data } = await getReportsData({
          page: listData?.current_page + 1,
        });
        setListData(data.response);
        setState("production");
      }
      return;
    },
    previous: async () => {
      if (listData?.current_page > 1) {
        setState("loading");

        const { data } = await getReportsData({
          page: listData?.current_page - 1,
        });
        setListData(data.response);
        setState("production");
      }
      return;
    },
    lastPage: async () => {
      if (listData?.current_page !== listData?.last_page) {
        setState("loading");

        const { data } = await getReportsData({ page: listData?.last_page });
        setListData(data?.response);
        setState("production");
      }
      return;
    },
  };

  return (
    <Main>
      <If condition={hasPermission("view-reports-list")}>
        <PageHeader
          icon={FileText}
          title="Relatorios"
          description="Gere e visualize relatorios"
        >
          <Button
            variant="outline"
            size="icon"
            onClick={handleRefresh}
            disabled={isRefreshing}
            title="Atualizar lista"
            className="h-8 w-8"
          >
            <RefreshCw
              className={`h-4 w-4 ${isRefreshing ? "animate-spin" : ""}`}
            />
          </Button>
          <If condition={hasPermission("generate-report")}>
            <AddBtn />
          </If>
        </PageHeader>
        <div className="flex flex-col gap-3">
          <SearchComponent
            updateFront={setListData}
            setState={setState}
            funcSearch={searchReportData}
            placeholder="Pesquisar"
          />
          {state === "loading" ? (
            <CreateLoaderTable cols={4} lines={6} />
          ) : (
            <TableData
              dataApi={listData}
              pageActions={pageActions}
              updateFront={setListData}
            />
          )}
        </div>
      </If>
    </Main>
  );
};
